/**
 * Page marketplace publique (catalogue produits)
 *
 * Rôle : Catalogue produits avec filtres (catégorie, marque, prix) ; panier, favoris ; personnalisation par secteur/métier (session).
 * Données produits en dur ou via API. Session pour recommandations. Technologies : React, next-auth, Next.js, Tailwind
 */
"use client";

import React, { useMemo, useState, useEffect } from "react";
import { ShoppingCart, Heart, Star, Truck, Award } from "lucide-react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";

type Product = {
  id: string;
  name: string;
  description: string;
  detailedDescription: string;
  specifications: Record<string, string>;
  stock: number;
  price: number;
  category: string;
  brand: string;
  weight: string;
  dimensions: string;
  color: string;
  material: string;
  warranty: string;
  certifications: string[];
  image: string;
  rating: number;
  reviews: number;
  isNew: boolean;
  isPopular: boolean;
};

type CartItem = {
  product: Product;
  quantity: number;
};

// Produits adaptés aux particuliers
const marketplaceProducts: Product[] = [
  {
    id: "mp-001",
    name: "Kit de navigation marine complet",
    description: "Kit complet pour la navigation avec GPS, compas et accessoires",
    detailedDescription: "Kit de navigation marine professionnel comprenant un GPS marin, un compas magnétique, des cartes marines et tous les accessoires nécessaires pour une navigation en toute sécurité. Parfait pour les plaisanciers débutants et expérimentés.",
    specifications: {
      "GPS": "12 canaux, écran couleur 5 pouces",
      "Compas": "Magnétique avec éclairage LED",
      "Cartes": "Côtes françaises incluses",
      "Autonomie": "8 heures",
      "Étanchéité": "IPX7",
      "Garantie": "2 ans"
    },
    stock: 15,
    price: 299.99,
    category: "Navigation",
    brand: "MarineTech",
    weight: "2.5 kg",
    dimensions: "30 x 20 x 15 cm",
    color: "Blanc et bleu",
    material: "Plastique ABS marin",
    warranty: "2 ans",
    certifications: ["CE", "IPX7"],
    image: "/logos/navigation-kit.jpg",
    rating: 4.8,
    reviews: 127,
    isNew: true,
    isPopular: true
  },
  {
    id: "mp-002",
    name: "Ancre pliable 15 kg",
    description: "Ancre pliable en acier galvanisé pour bateaux jusqu'à 8 mètres",
    detailedDescription: "Ancre pliable en acier galvanisé haute résistance, spécialement conçue pour les bateaux de plaisance jusqu'à 8 mètres. Design compact qui se plie facilement pour un rangement optimal. Tenue excellente sur tous types de fonds.",
    specifications: {
      "Poids": "15 kg",
      "Matériau": "Acier galvanisé",
      "Longueur pliée": "45 cm",
      "Longueur dépliée": "120 cm",
      "Tenue": "Tous fonds",
      "Bateaux": "Jusqu'à 8 mètres"
    },
    stock: 8,
    price: 189.99,
    category: "Ancrage",
    brand: "AnchorPro",
    weight: "15 kg",
    dimensions: "45 x 25 x 15 cm (pliée)",
    color: "Gris galvanisé",
    material: "Acier galvanisé",
    warranty: "5 ans",
    certifications: ["CE", "Marine Grade"],
    image: "/logos/ancre-pliable.jpg",
    rating: 4.6,
    reviews: 89,
    isNew: false,
    isPopular: true
  },
  {
    id: "mp-003",
    name: "Gilet de sauvetage auto-gonflant",
    description: "Gilet de sauvetage auto-gonflant avec déclenchement automatique",
    detailedDescription: "Gilet de sauvetage auto-gonflant haute performance avec déclenchement automatique au contact de l'eau. Confortable à porter, discret et conforme aux normes de sécurité maritime. Idéal pour la navigation de plaisance.",
    specifications: {
      "Type": "Auto-gonflant",
      "Déclenchement": "Automatique",
      "Flottabilité": "150N",
      "Taille": "Universelle (80-120 kg)",
      "Autonomie": "5 ans",
      "Norme": "EN ISO 12402-3"
    },
    stock: 25,
    price: 149.99,
    category: "Sécurité",
    brand: "SafeMarine",
    weight: "0.8 kg",
    dimensions: "30 x 25 x 8 cm",
    color: "Orange fluo",
    material: "Nylon technique",
    warranty: "5 ans",
    certifications: ["CE", "EN ISO 12402-3"],
    image: "/logos/gilet-sauvetage.jpg",
    rating: 4.9,
    reviews: 203,
    isNew: false,
    isPopular: true
  },
  {
    id: "mp-004",
    name: "Pompe de cale électrique 12V",
    description: "Pompe de cale automatique avec interrupteur flotteur",
    detailedDescription: "Pompe de cale électrique 12V haute performance avec système automatique de détection d'eau. Interrupteur à flotteur garantit un fonctionnement optimal même en cas d'inclinaison du navire. Installation facile et fiable.",
    specifications: {
      "Débit": "1100 L/h",
      "Tension": "12V DC",
      "Consommation": "120W",
      "Hauteur refoulement": "4 m",
      "Diamètre entrée": "32mm",
      "Diamètre sortie": "25mm"
    },
    stock: 12,
    price: 89.99,
    category: "Équipement",
    brand: "PumpTech",
    weight: "2.1 kg",
    dimensions: "19 x 12 x 18 cm",
    color: "Noir et argent",
    material: "Plastique ABS + métal",
    warranty: "2 ans",
    certifications: ["CE", "IP44"],
    image: "/logos/pompe-cale.jpg",
    rating: 4.7,
    reviews: 156,
    isNew: true,
    isPopular: false
  },
  {
    id: "mp-005",
    name: "Corde d'amarrage polyester 20m",
    description: "Corde d'amarrage en polyester haute résistance, 20 mètres",
    detailedDescription: "Corde d'amarrage en polyester haute résistance, idéale pour l'amarrage des bateaux de plaisance. Résistante aux UV, à l'eau de mer et aux frottements. Diamètre optimal pour une manipulation facile.",
    specifications: {
      "Longueur": "20 mètres",
      "Diamètre": "12 mm",
      "Matériau": "Polyester",
      "Résistance": "2000 kg",
      "Résistance UV": "100%",
      "Flottabilité": "Neutre"
    },
    stock: 30,
    price: 45.99,
    category: "Amarrage",
    brand: "RopeMaster",
    weight: "1.8 kg",
    dimensions: "20 m x 12 mm",
    color: "Bleu marine",
    material: "Polyester",
    warranty: "3 ans",
    certifications: ["CE", "Marine Grade"],
    image: "/logos/corde-amarrage.jpg",
    rating: 4.5,
    reviews: 78,
    isNew: false,
    isPopular: false
  },
  {
    id: "mp-006",
    name: "Kit d'outils marine",
    description: "Kit d'outils spécialisés pour l'entretien des bateaux",
    detailedDescription: "Kit complet d'outils spécialisés pour l'entretien et la réparation des bateaux. Tous les outils sont en acier inoxydable pour résister à l'environnement marin. Mallette étanche incluse.",
    specifications: {
      "Outils": "15 pièces",
      "Matériau": "Acier inoxydable",
      "Mallette": "Étanche IP65",
      "Poids total": "3.2 kg",
      "Garantie outils": "Lifetime",
      "Conformité": "Marine Grade"
    },
    stock: 6,
    price: 199.99,
    category: "Outils",
    brand: "ToolMarine",
    weight: "3.2 kg",
    dimensions: "40 x 25 x 15 cm",
    color: "Inox et noir",
    material: "Acier inoxydable",
    warranty: "Lifetime",
    certifications: ["CE", "Marine Grade"],
    image: "/logos/kit-outils.jpg",
    rating: 4.8,
    reviews: 94,
    isNew: true,
    isPopular: false
  },
  {
    id: "mp-007",
    name: "Éclairage LED marin",
    description: "Feux de navigation LED haute luminosité",
    detailedDescription: "Feux de navigation LED haute performance pour bateaux de plaisance. Consommation réduite, longue durée de vie et conformité aux normes de navigation. Installation simple et fiable.",
    specifications: {
      "Type": "LED haute luminosité",
      "Consommation": "5W",
      "Autonomie": "50 000 heures",
      "Étanchéité": "IP67",
      "Tension": "12V/24V",
      "Couleur": "Rouge/B Blanc"
    },
    stock: 18,
    price: 79.99,
    category: "Éclairage",
    brand: "LightMarine",
    weight: "0.5 kg",
    dimensions: "15 x 10 x 8 cm",
    color: "Blanc et rouge",
    material: "Aluminium anodisé",
    warranty: "3 ans",
    certifications: ["CE", "IP67", "COLREG"],
    image: "/logos/eclairage-led.jpg",
    rating: 4.6,
    reviews: 112,
    isNew: false,
    isPopular: true
  },
  {
    id: "mp-008",
    name: "VHF portable étanche",
    description: "Radio VHF portable étanche IPX7 avec GPS intégré",
    detailedDescription: "Radio VHF portable étanche avec GPS intégré et fonction DSC. Autonomie exceptionnelle, portée étendue et interface intuitive. Parfait pour la sécurité en mer et la communication.",
    specifications: {
      "Étanchéité": "IPX7",
      "GPS": "Intégré",
      "DSC": "Oui",
      "Autonomie": "12 heures",
      "Portée": "25 miles nautiques",
      "Canaux": "99 canaux"
    },
    stock: 10,
    price: 399.99,
    category: "Communication",
    brand: "ComMarine",
    weight: "0.6 kg",
    dimensions: "20 x 8 x 4 cm",
    color: "Jaune fluo",
    material: "Caoutchouc technique",
    warranty: "3 ans",
    certifications: ["CE", "IPX7", "DSC"],
    image: "/logos/vhf-portable.jpg",
    rating: 4.9,
    reviews: 167,
    isNew: true,
    isPopular: true
  }
];

const categories = ["Tous", "Navigation", "Ancrage", "Sécurité", "Équipement", "Amarrage", "Outils", "Éclairage", "Communication"];

function ProductCard({ product, onAddToCart, onProductClick }: { 
  product: Product; 
  onAddToCart: (product: Product) => void;
  onProductClick: (product: Product) => void;
}) {
  const [isFavorite, setIsFavorite] = useState(false);

  return (
    <div className="group relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 border-gray-200 hover:border-orange-300 hover:-translate-y-2">
      {/* Badges */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
        {product.isNew && (
          <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            Nouveau
          </span>
        )}
        {product.isPopular && (
          <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            Populaire
          </span>
        )}
      </div>

      {/* Bouton favoris */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsFavorite(!isFavorite);
        }}
        className="absolute top-3 right-3 z-10 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
      >
        <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-600'}`} />
      </button>

      {/* Image produit */}
      <div className="relative aspect-square bg-gray-50 cursor-pointer" onClick={() => onProductClick(product)}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent z-10"></div>
        <div className="w-full h-full bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center group-hover:from-orange-100 group-hover:to-red-100 transition-all duration-500">
          <div className="text-6xl text-orange-300 group-hover:text-orange-400 transition-colors duration-500">⚓</div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-4">
        <div className="mb-2">
          <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-2 cursor-pointer hover:text-orange-600" onClick={() => onProductClick(product)}>
            {product.name}
          </h3>
          <p className="text-xs text-gray-600 line-clamp-2 mb-2">{product.description}</p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-3 h-3 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
            ))}
          </div>
          <span className="text-xs text-gray-600 ml-1">
            {product.rating} ({product.reviews} avis)
          </span>
        </div>

        {/* Prix et stock */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-right">
            <span className="text-lg font-bold text-orange-600">€{product.price}</span>
          </div>
          <div className="text-right text-xs">
            <div className={`font-medium ${product.stock > 10 ? 'text-green-600' : product.stock > 0 ? 'text-orange-600' : 'text-red-600'}`}>
              {product.stock > 0 ? `${product.stock} en stock` : 'Rupture'}
            </div>
          </div>
        </div>

        {/* Bouton ajouter au panier */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddToCart(product);
          }}
          disabled={product.stock === 0}
          className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${
            product.stock > 0
              ? 'bg-gradient-to-r from-orange-400 to-red-500 text-white hover:from-orange-500 hover:to-red-600 hover:shadow-lg transform hover:-translate-y-0.5'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {product.stock > 0 ? (
            <div className="flex items-center justify-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              Ajouter au panier
            </div>
          ) : (
            'Rupture de stock'
          )}
        </button>
      </div>
    </div>
  );
}

function CartModal({ isOpen, onClose, cart, onRemoveItem, onUpdateQuantity }: {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  onRemoveItem: (productId: string) => void;
  onUpdateQuantity: (productId: string, quantity: number) => void;
}) {
  const subtotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const shipping = subtotal > 100 ? 0 : 9.99;
  const total = subtotal + shipping;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-orange-50 to-red-50">
          <h2 className="text-xl font-bold text-gray-900">Panier ({cart.length} articles)</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2 hover:bg-white/50 rounded-full transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh]">
          {cart.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">🛒</div>
              <p className="text-gray-500 text-lg">Votre panier est vide</p>
              <p className="text-gray-400 text-sm mt-2">Découvrez nos produits marins</p>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {cart.map((item) => (
                <div key={item.product.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-200 rounded-lg flex items-center justify-center">
                    <div className="text-2xl">⚓</div>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900">{item.product.name}</div>
                    <div className="text-xs text-gray-600">{item.product.brand}</div>
                    <div className="text-sm font-bold text-orange-600">€{item.product.price}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => onUpdateQuantity(item.product.id, item.quantity - 1)}
                      className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm hover:bg-gray-300 transition-colors"
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                    <button
                      onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)}
                      className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm hover:bg-gray-300 transition-colors"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => onRemoveItem(item.product.id)}
                    className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="p-6 border-t bg-gradient-to-r from-orange-50 to-red-50">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span>Sous-total:</span>
                <span>€{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Livraison:</span>
                <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>
                  {shipping === 0 ? 'Gratuite' : `€${shipping.toFixed(2)}`}
                </span>
              </div>
              {subtotal < 100 && (
                <div className="text-xs text-orange-600">
                  Livraison gratuite à partir de €100
                </div>
              )}
            </div>
            <div className="flex justify-between items-center mb-4 text-lg font-bold">
              <span>Total:</span>
              <span className="text-orange-600">€{total.toFixed(2)}</span>
            </div>
            <button className="w-full bg-gradient-to-r from-orange-400 to-red-500 text-white py-3 px-4 rounded-xl font-bold hover:from-orange-500 hover:to-red-600 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg">
              Commander maintenant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductDetailModal({ isOpen, onClose, product, onAddToCart }: {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onAddToCart: (product: Product) => void;
}) {
  const [selectedTab, setSelectedTab] = useState<'description' | 'specs' | 'reviews'>('description');

  if (!isOpen || product === null) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-orange-50 to-red-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{product.name}</h2>
            <p className="text-gray-700">{product.brand} • {product.category}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2 hover:bg-white/50 rounded-full transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Image et prix */}
          <div className="flex gap-6 mb-6">
            <div className="w-1/3">
              <div className="aspect-square bg-gradient-to-br from-orange-50 to-red-50 rounded-xl flex items-center justify-center">
                <div className="text-8xl text-orange-300">⚓</div>
              </div>
            </div>
            <div className="w-2/3">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                  ))}
                </div>
                <span className="text-gray-600">
                  {product.rating} ({product.reviews} avis)
                </span>
              </div>
              
              <div className="flex items-center gap-4 mb-4">
                <span className="text-3xl font-bold text-orange-600">€{product.price}</span>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  product.stock > 10 ? 'bg-green-100 text-green-700' : 
                  product.stock > 0 ? 'bg-orange-100 text-orange-700' : 
                  'bg-red-100 text-red-700'
                }`}>
                  {product.stock > 0 ? `${product.stock} en stock` : 'Rupture de stock'}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => onAddToCart(product)}
                  disabled={product.stock === 0}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all duration-200 ${
                    product.stock > 0
                      ? 'bg-gradient-to-r from-orange-400 to-red-500 text-white hover:from-orange-500 hover:to-red-600 hover:shadow-lg transform hover:-translate-y-0.5'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {product.stock > 0 ? (
                    <div className="flex items-center justify-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Ajouter au panier
                    </div>
                  ) : (
                    'Rupture de stock'
                  )}
                </button>
                <button className="p-3 border-2 border-gray-300 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-colors">
                  <Heart className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Onglets */}
          <div className="border-b mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setSelectedTab('description')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    selectedTab === 'description'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Description
              </button>
              <button
                onClick={() => setSelectedTab('specs')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'specs'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Spécifications
              </button>
              <button
                onClick={() => setSelectedTab('reviews')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'reviews'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Avis ({product.reviews})
              </button>
            </nav>
          </div>

          {/* Contenu des onglets */}
          <div className="overflow-y-auto max-h-[60vh]">
            {selectedTab === 'description' ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2 text-lg">Description</h3>
                  <p className="text-gray-700 leading-relaxed">{product.detailedDescription}</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2 text-lg">Informations générales</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="font-medium">Marque:</span> {product.brand}</div>
                    <div><span className="font-medium">Catégorie:</span> {product.category}</div>
                    <div><span className="font-medium">Poids:</span> {product.weight}</div>
                    <div><span className="font-medium">Dimensions:</span> {product.dimensions}</div>
                    <div><span className="font-medium">Couleur:</span> {product.color}</div>
                    <div><span className="font-medium">Matériau:</span> {product.material}</div>
                    <div><span className="font-medium">Garantie:</span> {product.warranty}</div>
                    <div>
                      <span className="font-medium">Certifications:</span>
                      <div className="flex gap-1 mt-1">
                        {product.certifications.map((cert, index) => (
                          <span key={index} className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded whitespace-nowrap">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : selectedTab === 'specs' ? (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Spécifications techniques</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-3 border-b border-gray-100">
                      <span className="font-medium text-gray-900">{key}:</span>
                      <span className="text-gray-700">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Avis clients</h3>
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-4">⭐</div>
                  <p>Les avis clients seront bientôt disponibles</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MarketplacePage() {
  const router = useRouter();
  const { data: session } = useSession();
  const [checking, setChecking] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tous");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);

  // Vérifier le plan de l'utilisateur
  useEffect(() => {
    const checkPlan = async () => {
      if (!session?.user?.id) {
        setChecking(false);
        return;
      }

      try {
        const userRes = await fetch('/api/users/me');
        const userData = await userRes.json();
        if (userData.success) {
          const plan = userData.subscription?.plan || userData.user?.plan || 'Découverte';
          if (plan === 'Découverte') {
            // Rediriger vers le dashboard si plan Découverte
            router.push('/dashboard');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking user plan:', error);
      } finally {
        setChecking(false);
      }
    };

    checkPlan();
  }, [session, router]);

  const filteredProducts = useMemo(() => {
    return marketplaceProducts.filter(product => {
      const matchesSearch = !searchQuery || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === "Tous" || product.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, marketplaceProducts]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.product.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => prev.filter(item => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prev => prev.map(item => 
      item.product.id === productId ? { ...item, quantity } : item
    ));
  };

  const openProductDetail = (product: Product) => {
    setSelectedProduct(product);
    setShowProductDetail(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header>
        <div className="mx-auto max-w-7xl px-6 py-4">
          {/* Header vide pour l'espacement */}
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-2xl md:text-3xl font-extrabold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-4">
            Marketplace
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Découvrez notre sélection d&apos;équipements marins de qualité pour votre bateau. 
            Navigation, sécurité, ancrage et bien plus encore.
          </p>
          
          {/* Avantages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="flex items-center justify-center gap-3 bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-6 shadow-lg border-2 border-orange-200 hover:border-orange-300 transition-all duration-300">
              <Truck className="w-6 h-6 text-orange-600" />
              <span className="font-medium text-gray-900">Livraison rapide</span>
            </div>
            <div className="flex items-center justify-center gap-3 bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-6 shadow-lg border-2 border-orange-200 hover:border-orange-300 transition-all duration-300">
              <Award className="w-6 h-6 text-orange-600" />
              <span className="font-medium text-gray-900">Qualité marine</span>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl shadow-lg border-2 border-orange-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit..."
                className="w-full rounded-xl border border-orange-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="rounded-xl border border-orange-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Produits */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Produits ({filteredProducts.length})
            </h2>
            <div className="text-sm text-orange-600 font-medium">
              Livraison gratuite à partir de €100
            </div>
          </div>
          
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun produit trouvé</h3>
              <p className="text-gray-600">Essayez de modifier vos critères de recherche</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onAddToCart={addToCart} 
                  onProductClick={openProductDetail}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl shadow-lg border-2 border-orange-200 p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Besoin d&apos;aide ?</h3>
          <p className="text-gray-700 mb-6">
            Notre équipe d&apos;experts marins est là pour vous conseiller
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-6 py-3 rounded-xl font-medium hover:from-orange-500 hover:to-red-600 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg">
              Nous contacter
            </button>
            <button className="border-2 border-orange-400 text-orange-600 px-6 py-3 rounded-xl font-medium hover:bg-orange-50 transition-colors">
              Guide d&apos;achat
            </button>
          </div>
        </div>
      </div>

      {/* Bouton panier fixe */}
      <button
        onClick={() => setShowCart(true)}
        className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-orange-400 to-red-500 text-white px-6 py-4 rounded-3xl font-bold shadow-2xl hover:from-orange-500 hover:to-red-600 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-3xl flex items-center gap-3"
      >
        <ShoppingCart className="w-6 h-6" />
        <span className="hidden sm:inline">Panier ({cart.length})</span>
        <span className="sm:hidden">({cart.length})</span>
        {cart.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-white text-orange-600 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-orange-500">
            {cart.length}
          </span>
        )}
      </button>

      {/* Modales */}
      <ProductDetailModal
        isOpen={showProductDetail}
        onClose={() => setShowProductDetail(false)}
        product={selectedProduct}
        onAddToCart={addToCart}
      />

      <CartModal
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        cart={cart}
        onRemoveItem={removeFromCart}
        onUpdateQuantity={updateQuantity}
      />
    </div>
  );
}
